/**
 * ETP Page Teasers – accordion, expand-all, and show-all behaviour.
 *
 * Three responsibilities:
 *
 *   1. Accordion (mobile):
 *      Clicking .etp-pt__header toggles .is-open on the parent .etp-pt__item.
 *      On desktop the body is always visible via CSS, so the toggle has no
 *      visual effect and can safely run at all viewport widths.
 *
 *   2. Expand / Collapse all (mobile):
 *      Clicking .etp-pt__expand-all toggles .is-open on every item.
 *      Button text swaps between data-more / data-less values.
 *
 *   3. Toggle overflow (both breakpoints):
 *      Overflow items render with hidden="until-found" so find-in-page can locate
 *      them while collapsed. Clicking .etp-pt__toggle clears/sets the attribute
 *      and toggles .is-expanded on the root; a beforematch event reveals them all
 *      when a search match lands inside a collapsed item.
 *      Button text swaps between data-more / data-less; aria-expanded tracks state.
 */

export function initPageTeasers( scope ) {
  // Find all page-teaser roots that have not yet been initialised.
  const roots = ( scope || document ).querySelectorAll(
    '.etp-pt:not([data-etp-pt-ready])'
  );

  roots.forEach( ( root ) => {

    // ── 1. Accordion toggle ──────────────────────────────────────────────────

    root.querySelectorAll( '.etp-pt__header' ).forEach( ( header ) => {
      header.addEventListener( 'click', () => {
        const item = header.closest( '.etp-pt__item' );
        if ( item ) {
          item.classList.toggle( 'is-open' );
        }
      } );
    } );

    // ── 2. Expand / Collapse all ─────────────────────────────────────────────

    const expandAllBtn = root.querySelector( '.etp-pt__expand-all' );
    if ( expandAllBtn ) {
      expandAllBtn.dataset.expanded = 'false';
      expandAllBtn.addEventListener( 'click', () => {
        const isExpanded = expandAllBtn.dataset.expanded === 'true';
        root.querySelectorAll( '.etp-pt__item' ).forEach( ( item ) => {
          item.classList.toggle( 'is-open', ! isExpanded );
        } );
        expandAllBtn.dataset.expanded = String( ! isExpanded );
        expandAllBtn.textContent = isExpanded
          ? ( expandAllBtn.dataset.more || 'Expand all' )
          : ( expandAllBtn.dataset.less || 'Collapse all' );
      } );
    }

    // ── 3. Toggle overflow (show more / show less) ───────────────────────────

    const toggleBtn = root.querySelector( '.etp-pt__toggle' );
    const overflowItems = () => root.querySelectorAll( '.etp-pt__item--overflow' );

    const setOverflowExpanded = ( expanded ) => {
      overflowItems().forEach( ( item ) => {
        if ( expanded ) {
          item.removeAttribute( 'hidden' );
        } else {
          item.setAttribute( 'hidden', 'until-found' );
        }
      } );
      root.classList.toggle( 'is-expanded', expanded );
      if ( toggleBtn ) {
        toggleBtn.setAttribute( 'aria-expanded', String( expanded ) );
        toggleBtn.textContent = expanded
          ? ( toggleBtn.dataset.less || 'Show less' )
          : ( toggleBtn.dataset.more || 'Show all' );
      }
    };

    if ( toggleBtn ) {
      toggleBtn.addEventListener( 'click', () => {
        const willExpand = ! root.classList.contains( 'is-expanded' );
        setOverflowExpanded( willExpand );
        if ( ! willExpand ) {
          root.scrollIntoView( { behavior: 'smooth', block: 'start' } );
        }
      } );
    }

    // Find-in-page or a text-fragment link matched a collapsed overflow item:
    // the browser removed its hidden attribute, so reveal them all and sync state.
    overflowItems().forEach( ( item ) => {
      item.addEventListener( 'beforematch', () => setOverflowExpanded( true ) );
    } );

    // Mark this root as initialised so re-renders don't double-bind.
    root.dataset.etpPtReady = 'true';
  } );
}
